import { useEffect } from "react";
import { LOGO_URL } from "@/lib/quoteContent";
import { initSidePageTracking } from "@/lib/tracker";
import { Server, Cloud, InfinityIcon, ShieldCheck, ArrowLeft } from "lucide-react";

const STEPS = [
  {
    n: "STEP 01",
    icon: Server,
    title: "The moment we get home",
    label: "ONSITE · 2X REDUNDANT",
    body: "Your wedding footage is immediately backed up TWICE on our onsite server. Two independent copies, on separate drives, before anyone sleeps.",
  },
  {
    n: "STEP 02",
    icon: Cloud,
    title: "Straight to the cloud",
    label: "AMAZON AWS · SECURE STORAGE",
    body: "At the same time, everything is uploaded to Amazon AWS secure storage. The same infrastructure trusted by banks and governments. Even if our studio burned down, your wedding wouldn't.",
  },
  {
    n: "STEP 03",
    icon: InfinityIcon,
    title: "Backed up forever",
    label: "OFFSITE CLOUD · ETERNAL",
    body: "Once your film is edited, your finished wedding collection is backed up eternally on our offsite cloud backup server. FOREVER. Not for a year. Not for ten. Forever.",
  },
];

export default function LifetimeGuarantee() {
  useEffect(() => initSidePageTracking("guarantee"), []);
  return (
    <div
      className="min-h-screen bg-[#191818] text-white"
      data-testid="lifetime-guarantee-page"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    >
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center relative">
        <button
          type="button"
          onClick={() => window.close()}
          data-testid="guarantee-back-top-btn"
          className="absolute top-6 left-6 md:top-8 md:left-12 inline-flex items-center gap-2 text-white/70 hover:text-white border border-white/30 hover:border-white/70 px-5 py-3 text-xs tracking-overline uppercase font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back To Your Quote
        </button>
        <img
          src={LOGO_URL}
          alt="Wedding Video Essex"
          className="h-20 w-auto object-contain brightness-0 invert opacity-95 mx-auto mb-12"
        />
        <div className="inline-flex items-center gap-2 border border-[#2E7D32]/60 bg-[#2E7D32]/10 px-4 py-2 mb-8">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4CAF50] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4CAF50]" />
          </span>
          <span className="font-mono text-[11px] tracking-widest text-[#4CAF50] uppercase">
            All systems operational
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight max-w-3xl mx-auto mb-8">
          Your wedding, guaranteed
          <br />
          <span className="italic font-light">for life.</span>
        </h1>
        <p className="text-white/60 max-w-2xl mx-auto text-lg leading-relaxed">
          This page is for the geeks (and the worriers). Here's exactly what
          happens to your wedding footage from the moment we film it, and why
          it can never, ever be lost.
        </p>
        <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl mx-auto font-mono text-center">
          {[
            ["3X", "COPIES MINIMUM"],
            ["0MS", "TO FIRST BACKUP"],
            ["LIFETIME", "RETENTION PERIOD"],
          ].map(([v, l]) => (
            <div key={l} className="border border-white/10 bg-white/[0.03] py-5">
              <div className="text-[#CCBA56] text-2xl md:text-3xl">{v}</div>
              <div className="text-white/40 text-[10px] tracking-widest mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section className="px-6 md:px-12 lg:px-24 pb-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="border border-white/10 bg-[#1F1E1D]/80 p-8 md:p-10">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-[11px] tracking-widest text-[#CCBA56]">{s.n}</span>
                  <span className="font-mono text-[10px] tracking-widest text-white/40 border border-white/15 px-3 py-1.5">
                    {s.label}
                  </span>
                </div>
                <div className="flex items-start gap-5">
                  <Icon className="w-8 h-8 text-[#CCBA56] shrink-0 mt-1" strokeWidth={1.2} />
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-medium mb-3">{s.title}</h2>
                    <p className="text-white/60 leading-relaxed font-light">{s.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Guarantee */}
      <section className="px-6 md:px-12 lg:px-24 py-20">
        <div className="max-w-3xl mx-auto border border-[#CCBA56]/40 bg-[#CCBA56]/5 p-10 md:p-14 text-center">
          <ShieldCheck className="w-10 h-10 text-[#CCBA56] mx-auto mb-6" strokeWidth={1.2} />
          <div className="text-[#CCBA56] text-xs tracking-overline uppercase font-medium mb-4">
            The Lifetime Wedding Guarantee
          </div>
          <p className="font-display text-2xl md:text-3xl leading-snug font-light mb-6">
            We guarantee you'll have access to your wedding collection for
            life.
          </p>
          <p className="text-white/60 leading-relaxed font-light max-w-xl mx-auto">
            Lost your USB? Phone died? House move chaos? Just ask, and we'll
            restore it. And if your grandkids need access in the future,
            they'll have access too.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 pb-20 text-center">
        <button
          type="button"
          onClick={() => window.close()}
          data-testid="guarantee-back-btn"
          className="inline-flex items-center gap-2 border border-white/40 hover:border-white text-white px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors"
        >
          Back To Your Quote
        </button>
      </section>
    </div>
  );
}
